import { Col, Form, Input, Row, Tooltip } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DraggableModal } from '../../../components/DraggableModal'
import { EntRole } from '../../../models/role'
import SelectGovernment from '../../../components/selects/SelectGovernment'
import { aliasText } from '../../../utils'
import { RolesAvailableRoutes } from './RolesAvailableRoutes'
import { EntRoleAvailableRoutes } from '../../../models/rolesAvailableRoutes'

enum FormField {
	alias,
	nameKy,
	nameRu,
	governmentIds,
	passwordLength,
	availableRoutes,
}

interface IProps {
	show: boolean
	role: EntRole
	onHide: () => void
	onSubmit: (pos: EntRole) => Promise<boolean>
}

export const AddRoleModal = (props: IProps) => {
	const { t } = useTranslation()
	const [form] = useForm()
	const [loading, setLoading] = useState<boolean>(false)

	const [selectedRoutes, setSelectedRoutes] = useState<EntRoleAvailableRoutes[]>(
		Array.isArray(props.role.availableRoutes) ? props.role.availableRoutes : []
	)

	useEffect(() => {
		if (props.show) {
			setFieldsFromEntity()
			setSelectedRoutes(Array.isArray(props.role.availableRoutes) ? props.role.availableRoutes : [])
		}
	}, [props.show])

	const setFieldsFromEntity = (): void => {
		form.setFields([
			{ name: FormField.alias, value: props.role.alias },
			{ name: FormField.nameKy, value: props.role.nameKy },
			{ name: FormField.nameRu, value: props.role.nameRu },
			{ name: FormField.passwordLength, value: props.role.passwordLength },
			{ name: FormField.governmentIds, value: props.role.governments?.map(g => g.id) || [] },
			// { name: FormField.availableRoutes, value: selectedRoutes },
		])
	}

	const setEntityFromFields = (): EntRole => {
		let role = _.cloneDeep(props.role)
		role.alias = form.getFieldValue(FormField.alias)
		role.nameKy = form.getFieldValue(FormField.nameKy)
		role.nameRu = form.getFieldValue(FormField.nameRu)
		role.governmentIds = form.getFieldValue(FormField.governmentIds)
		role.passwordLength = form.getFieldValue(FormField.passwordLength)
		role.availableRoutes = selectedRoutes
		return role
	}

	const handleSubmit = async () => {
		setLoading(true)
		try {
			await form.validateFields()
			const data = setEntityFromFields()

			// Если id = 0 (новая роль), нужно всем availableRoutes проставить id = null
			if (!data.id) {
				data.availableRoutes?.forEach(r => {
					r.id = null // или как ваш сервер ожидает
				})
			}

			const result = await props.onSubmit(data)
			if (result) props.onHide()
		} finally {
			setLoading(false)
		}
	}

	const handleRouteChange = (selectedRoutes: EntRoleAvailableRoutes[]) => {
		setSelectedRoutes(selectedRoutes)
	}

	return (
		<>
			<DraggableModal
				title={
					props.role.id === null || props.role.id === 0 ? t('role.add_role') : t('role.edit_role')
				}
				visible={props.show}
				onCancel={props.onHide}
				onSubmit={handleSubmit}
				submitLoading={loading}
				width={1000}
			>
				<Form initialValues={{ remember: true }} autoComplete='off' layout='vertical' form={form}>
					<Row gutter={12}>
						<Col span={10}>
							<Tooltip title={aliasText}>
								<Form.Item
									label={t('general.alias')}
									name={FormField.alias}
									rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
								>
									<Input maxLength={255} showCount autoComplete='off' />
								</Form.Item>
							</Tooltip>
							<Form.Item
								label={t('user.organization')}
								name={FormField.governmentIds}
								rules={[{ required: false, message: t('msg.fill_in_the_field') }]}
							>
								<SelectGovernment mode='multiple' />
							</Form.Item>
							<Form.Item
								label={t('general.passwordLength')}
								name={FormField.passwordLength}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<Input
									onChange={e => {
										let value = e.target.value.replace(/\D/g, '').slice(0, 2) // Убираем не цифры и ограничиваем 2 символами
										form.setFieldsValue({ [FormField.passwordLength]: value })
									}}
									value={form.getFieldValue(FormField.passwordLength)}
									showCount
									maxLength={2}
									autoComplete='off'
								/>
							</Form.Item>
						</Col>
						<Col span={10}>
							<Form.Item
								label={t('general.nameKy')}
								name={FormField.nameKy}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<Input maxLength={255} showCount autoComplete='off' />
							</Form.Item>
							<Form.Item
								label={t('general.nameRu')}
								name={FormField.nameRu}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<Input maxLength={255} showCount autoComplete='off' />
							</Form.Item>
						</Col>
					</Row>
					<RolesAvailableRoutes selectedRoutes={selectedRoutes} onRouteChange={handleRouteChange} />
				</Form>
			</DraggableModal>
		</>
	)
}
